import React from "react"
import { notNull } from "@digits-shared/helpers/filters"

export const MetaTags: React.FC<React.PropsWithChildren> = ({ children }) => {
  const headTags = React.useRef<HTMLElement[]>([])

  React.useEffect(() => {
    const headNode = document.head
    if (!headNode) return

    headTags.current = []
    // Create and append new head tags
    if (children) {
      React.Children.forEach(children, (child) => {
        if (React.isValidElement(child) && (child.type === "meta" || child.type === "link")) {
          const element = document.createElement(child.type)

          // Copy all props to the element
          const props = child.props as Record<string, string>
          Object.entries(props).forEach(([key, value]) => {
            if (key !== "children") {
              element.setAttribute(key, value)
            }
          })
          headTags.current.push(element)
        }
      })
    }

    const oldTags = headTags.current
      .map((tag) => {
        const name = tag.attributes?.getNamedItem("name")?.value
        const rel = tag.attributes?.getNamedItem("rel")?.value
        const media = tag.attributes?.getNamedItem("media")?.value
        if (!name && !rel) return null
        if (tag.tagName === "META") {
          return `meta[name='${name}']`
        }
        // For link tags, include media in the selector if it exists
        return media ? `link[rel='${rel}'][media='${media}']` : `link[rel='${rel}']`
      })
      .filter(notNull)
      .flatMap((query) => Array.from(document.querySelectorAll(query)))

    // Check if old and new tags are the same
    const areTagsEqual = () => {
      if (oldTags.length !== headTags.current.length) return false

      return headTags.current.every((newTag) => {
        const name = newTag.attributes?.getNamedItem("name")?.value
        const rel = newTag.attributes?.getNamedItem("rel")?.value
        const media = newTag.attributes?.getNamedItem("media")?.value
        if (!name && !rel) return false

        const oldTag = oldTags.find((old) => {
          const oldName = old.attributes?.getNamedItem("name")?.value
          const oldRel = old.attributes?.getNamedItem("rel")?.value
          const oldMedia = old.attributes?.getNamedItem("media")?.value

          if (newTag.tagName === "META") {
            return name && oldName === name
          }
          // For link tags, match both rel and media if it exists
          return rel && oldRel === rel && (!media || oldMedia === media)
        })
        if (!oldTag) return false

        // Compare contents
        return (
          newTag.attributes?.getNamedItem("content")?.value ===
          oldTag.attributes?.getNamedItem("content")?.value
        )
      })
    }

    // Only modify DOM if tags have changed
    if (!areTagsEqual()) {
      oldTags.forEach((oldTag) => {
        oldTag.parentNode?.removeChild(oldTag)
      })

      headTags.current.forEach((tag) => {
        document.head.appendChild(tag)
      })
    }

    return () => {
      // Remove added tags on unmount.
      headTags.current.forEach((tag) => {
        tag.parentNode?.removeChild(tag)
      })

      // restore old tags
      oldTags.forEach((tag) => {
        document.head.appendChild(tag)
      })
    }
  }, [children])

  return null
}
